import { useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { getErrorMessage } from "@/lib/utils";
import { Car, CheckCircle } from "@phosphor-icons/react";
import BrandLogo from "@/components/BrandLogo";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const inviteToken = searchParams.get("invite_token") || "";
  const invitedEmail = searchParams.get("email") || "";
  const inviteOrg = searchParams.get("org") || "";
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const resetSuccess = searchParams.get("reset") === "success";
  const inviteBanner = useMemo(() => {
    if (!inviteToken) return null;
    if (inviteOrg) {
      return `Sign in to join ${inviteOrg}.`;
    }
    return "Sign in with the invited email to accept your team invitation.";
  }, [inviteOrg, inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, inviteToken);
      navigate("/dashboard");
    } catch (err) {
      setError(getErrorMessage(err, "Invalid credentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm space-y-8">
          <BrandLogo
            iconClassName="w-10 h-10 rounded-lg"
            textClassName="text-2xl font-extrabold tracking-tight font-heading text-slate-900"
          />
          <div>
            <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-1 font-medium">Sign in to your rental operations desk</p>
          </div>
          {resetSuccess && (
            <div data-testid="login-reset-success" className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-4 py-3 rounded-md font-medium flex items-start gap-2">
              <CheckCircle weight="fill" className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
              Password updated — sign in with your new password.
            </div>
          )}
          {inviteBanner && (
            <div data-testid="login-invite-banner" className="bg-foreground/5 border border-black/10 text-slate-700 text-sm px-4 py-3 rounded-md font-medium">
              {inviteBanner}
            </div>
          )}
          {error && <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md font-medium">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Email</label>
              <input data-testid="login-email" type="email" autoComplete="email" required className="w-full h-12 px-4 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black font-medium" placeholder="admin@rentalinspect.com.au" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Password</label>
                <Link to="/forgot-password" className="text-xs font-bold text-foreground hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <input data-testid="login-password" type="password" autoComplete="current-password" required className="w-full h-12 px-4 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black font-medium" placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} />
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-12 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors">
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
          <p className="text-sm text-slate-500 text-center font-medium">
            No account? <Link to="/signup" className="text-foreground font-bold hover:underline">Create one</Link>
          </p>
        </div>
      </div>
      {/* Right - Hero */}
      <div className="hidden lg:flex flex-1 bg-slate-900 items-center justify-center p-12 relative overflow-hidden">
        <div className="relative z-10 text-center space-y-6 max-w-md">
          <Car weight="duotone" className="w-24 h-24 text-foreground mx-auto" />
          <h2 className="text-3xl font-black tracking-tight font-heading text-white">Fleet Operations Made Simple</h2>
          <p className="text-slate-400 text-base font-medium leading-relaxed">Manage rentals, track vehicles, handle inspections and agreements from one clean dashboard. Built for Australian rental businesses.</p>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-black/10 to-transparent" />
      </div>
    </div>
  );
}
